/* eslint-disable @typescript-eslint/no-unsafe-assignment -- EditingRecord is Record<string, any> from server props */
import { useForm } from "@inertiajs/react"
import { useEffect } from "react"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"

/* ── Types ──────────────────────────────────────────────── */

interface ContactTypeOption {
  id: number
  name: string
}

interface ContactTypes {
  email: ContactTypeOption[]
  phone: ContactTypeOption[]
  social: ContactTypeOption[]
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type EditingRecord = Record<string, any> | null

interface ModalProps {
  open: boolean
  onClose: () => void
  caseId: number
  subjectId: number
  editingRecord?: EditingRecord
}

interface ContactModalProps extends ModalProps {
  contactTypes: ContactTypeOption[]
  category: string
}

/* ── Shared ─────────────────────────────────────────────── */

function FieldError({ error }: { error?: string | string[] }) {
  if (!error) return null
  const message = Array.isArray(error) ? error[0] : error
  return <p className="text-xs text-red-600">{message}</p>
}

function FormRow({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-2">{children}</div>
}

function FormGrid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-2 gap-4">{children}</div>
}

/* ── Address Modal ──────────────────────────────────────── */

function AddAddressModal({
  open,
  onClose,
  caseId,
  subjectId,
  editingRecord,
}: ModalProps) {
  const isEditing = !!editingRecord
  const { data, setData, post, patch, processing, errors, reset, transform } =
    useForm({
      street: "",
      street2: "",
      city: "",
      state: "",
      zip: "",
      subdivision: "",
      dates: "",
      notes: "",
    })

  useEffect(() => {
    if (open && editingRecord) {
      setData({
        street: editingRecord.street || "",
        street2: editingRecord.street2 || "",
        city: editingRecord.city || "",
        state: editingRecord.state || "",
        zip: editingRecord.zip || "",
        subdivision: editingRecord.subdivision || "",
        dates: editingRecord.dates || "",
        notes: editingRecord.notes || "",
      })
    } else if (open && !editingRecord) {
      reset()
    }
  }, [open, editingRecord?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  transform((data) => ({ subject_address: data }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const basePath = `/cases/${caseId}/subjects/${subjectId}/addresses`
    const options = {
      onSuccess: () => {
        reset()
        onClose()
      },
    }

    if (isEditing) {
      patch(`${basePath}/${editingRecord.id}`, options)
    } else {
      post(basePath, options)
    }
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit" : "Add"} Address</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <FormRow>
            <Label>Street</Label>
            <Input
              value={data.street}
              onChange={(e) => setData("street", e.target.value)}
            />
            <FieldError error={errors.street} />
          </FormRow>
          <FormRow>
            <Label>Street 2</Label>
            <Input
              value={data.street2}
              onChange={(e) => setData("street2", e.target.value)}
            />
          </FormRow>
          <FormGrid>
            <FormRow>
              <Label>City</Label>
              <Input
                value={data.city}
                onChange={(e) => setData("city", e.target.value)}
              />
            </FormRow>
            <FormGrid>
              <FormRow>
                <Label>State</Label>
                <Input
                  value={data.state}
                  onChange={(e) => setData("state", e.target.value)}
                  maxLength={2}
                />
              </FormRow>
              <FormRow>
                <Label>Zip</Label>
                <Input
                  value={data.zip}
                  onChange={(e) => setData("zip", e.target.value)}
                />
              </FormRow>
            </FormGrid>
          </FormGrid>
          <FormRow>
            <Label>Subdivision</Label>
            <Input
              value={data.subdivision}
              onChange={(e) => setData("subdivision", e.target.value)}
            />
          </FormRow>
          <FormRow>
            <Label>Dates</Label>
            <Input
              value={data.dates}
              onChange={(e) => setData("dates", e.target.value)}
            />
          </FormRow>
          <FormRow>
            <Label>Notes</Label>
            <Textarea
              value={data.notes}
              onChange={(e) => setData("notes", e.target.value)}
              rows={2}
            />
          </FormRow>
          <DialogFooter>
            <Button variant="secondary" type="button" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={processing}
              className="bg-hpi-active-blue hover:bg-hpi-dark-blue text-white"
            >
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/* ── Alias Modal ────────────────────────────────────────── */

function AddAliasModal({
  open,
  onClose,
  caseId,
  subjectId,
  editingRecord,
}: ModalProps) {
  const isEditing = !!editingRecord
  const { data, setData, post, patch, processing, errors, reset, transform } =
    useForm({
      prefix: "",
      first_name: "",
      middle_name: "",
      last_name: "",
      suffix: "",
      notes: "",
    })

  useEffect(() => {
    if (open && editingRecord) {
      setData({
        prefix: editingRecord.prefix || "",
        first_name: editingRecord.first_name || "",
        middle_name: editingRecord.middle_name || "",
        last_name: editingRecord.last_name || "",
        suffix: editingRecord.suffix || "",
        notes: editingRecord.notes || "",
      })
    } else if (open && !editingRecord) {
      reset()
    }
  }, [open, editingRecord?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  transform((data) => ({ subject_alias: data }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const basePath = `/cases/${caseId}/subjects/${subjectId}/aliases`
    const options = {
      onSuccess: () => {
        reset()
        onClose()
      },
    }

    if (isEditing) {
      patch(`${basePath}/${editingRecord.id}`, options)
    } else {
      post(basePath, options)
    }
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit" : "Add"} Alias</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <FormGrid>
            <FormRow>
              <Label>Prefix</Label>
              <Input
                value={data.prefix}
                onChange={(e) => setData("prefix", e.target.value)}
              />
            </FormRow>
            <FormRow>
              <Label>Suffix</Label>
              <Input
                value={data.suffix}
                onChange={(e) => setData("suffix", e.target.value)}
              />
            </FormRow>
          </FormGrid>
          <FormRow>
            <Label>First Name</Label>
            <Input
              value={data.first_name}
              onChange={(e) => setData("first_name", e.target.value)}
            />
            <FieldError error={errors.first_name} />
          </FormRow>
          <FormRow>
            <Label>Middle Name</Label>
            <Input
              value={data.middle_name}
              onChange={(e) => setData("middle_name", e.target.value)}
            />
          </FormRow>
          <FormRow>
            <Label>Last Name</Label>
            <Input
              value={data.last_name}
              onChange={(e) => setData("last_name", e.target.value)}
            />
            <FieldError error={errors.last_name} />
          </FormRow>
          <FormRow>
            <Label>Notes</Label>
            <Textarea
              value={data.notes}
              onChange={(e) => setData("notes", e.target.value)}
              rows={2}
            />
          </FormRow>
          <DialogFooter>
            <Button variant="secondary" type="button" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={processing}
              className="bg-hpi-active-blue hover:bg-hpi-dark-blue text-white"
            >
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/* ── Contact Modal (shared for email/phone/social) ──────── */

function AddContactModal({
  open,
  onClose,
  caseId,
  subjectId,
  contactTypes,
  category,
  editingRecord,
}: ContactModalProps) {
  const isEditing = !!editingRecord
  const { data, setData, post, patch, processing, errors, reset, transform } =
    useForm({
      contact_type_id: "",
      info: "",
      notes: "",
    })

  useEffect(() => {
    if (open && editingRecord) {
      setData({
        contact_type_id: editingRecord.contact_type_id
          ? String(editingRecord.contact_type_id)
          : "",
        info: editingRecord.info || "",
        notes: editingRecord.notes || "",
      })
    } else if (open && !editingRecord) {
      reset()
    }
  }, [open, editingRecord?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  transform((data) => ({ subject_contact: data }))

  const labelMap: Record<string, string> = {
    email: "Email Address",
    phone: "Phone Number",
    social: "Handle / URL",
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const basePath = `/cases/${caseId}/subjects/${subjectId}/contacts`
    const options = {
      onSuccess: () => {
        reset()
        onClose()
      },
    }

    if (isEditing) {
      patch(`${basePath}/${editingRecord.id}`, options)
    } else {
      post(basePath, options)
    }
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Edit" : "Add"}{" "}
            {category === "email"
              ? "Email"
              : category === "phone"
                ? "Phone"
                : "Social Media"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <FormRow>
            <Label>Type</Label>
            <Select
              value={data.contact_type_id}
              onValueChange={(v) => setData("contact_type_id", v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select type..." />
              </SelectTrigger>
              <SelectContent>
                {contactTypes.map((ct) => (
                  <SelectItem key={ct.id} value={String(ct.id)}>
                    {ct.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldError error={errors.contact_type_id} />
          </FormRow>
          <FormRow>
            <Label>{labelMap[category] ?? "Info"}</Label>
            <Input
              value={data.info}
              onChange={(e) => setData("info", e.target.value)}
            />
            <FieldError error={errors.info} />
          </FormRow>
          <FormRow>
            <Label>Notes</Label>
            <Textarea
              value={data.notes}
              onChange={(e) => setData("notes", e.target.value)}
              rows={2}
            />
          </FormRow>
          <DialogFooter>
            <Button variant="secondary" type="button" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={processing}
              className="bg-hpi-active-blue hover:bg-hpi-dark-blue text-white"
            >
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/* ── Vehicle Modal ──────────────────────────────────────── */

function AddVehicleModal({
  open,
  onClose,
  caseId,
  subjectId,
  editingRecord,
}: ModalProps) {
  const isEditing = !!editingRecord
  const { data, setData, post, patch, processing, errors, reset, transform } =
    useForm({
      description: "",
      tag: "",
      tag_state: "",
      notes: "",
    })

  useEffect(() => {
    if (open && editingRecord) {
      setData({
        description: editingRecord.description || "",
        tag: editingRecord.tag || "",
        tag_state: editingRecord.tag_state || "",
        notes: editingRecord.notes || "",
      })
    } else if (open && !editingRecord) {
      reset()
    }
  }, [open, editingRecord?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  transform((data) => ({ subject_vehicle: data }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const basePath = `/cases/${caseId}/subjects/${subjectId}/vehicles`
    const options = {
      onSuccess: () => {
        reset()
        onClose()
      },
    }

    if (isEditing) {
      patch(`${basePath}/${editingRecord.id}`, options)
    } else {
      post(basePath, options)
    }
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit" : "Add"} Vehicle</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <FormRow>
            <Label>Description</Label>
            <Input
              value={data.description}
              onChange={(e) => setData("description", e.target.value)}
            />
            <FieldError error={errors.description} />
          </FormRow>
          <FormGrid>
            <FormRow>
              <Label>Tag</Label>
              <Input
                value={data.tag}
                onChange={(e) => setData("tag", e.target.value)}
              />
            </FormRow>
            <FormRow>
              <Label>Tag State</Label>
              <Input
                value={data.tag_state}
                onChange={(e) => setData("tag_state", e.target.value)}
                maxLength={2}
              />
            </FormRow>
          </FormGrid>
          <FormRow>
            <Label>Notes</Label>
            <Textarea
              value={data.notes}
              onChange={(e) => setData("notes", e.target.value)}
              rows={2}
            />
          </FormRow>
          <DialogFooter>
            <Button variant="secondary" type="button" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={processing}
              className="bg-hpi-active-blue hover:bg-hpi-dark-blue text-white"
            >
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/* ── Employment Modal ───────────────────────────────────── */

function AddEmploymentModal({
  open,
  onClose,
  caseId,
  subjectId,
  editingRecord,
}: ModalProps) {
  const isEditing = !!editingRecord
  const { data, setData, post, patch, processing, errors, reset, transform } =
    useForm({
      company_name: "",
      address: "",
      city: "",
      state: "",
      zip: "",
      phone: "",
      start_date: "",
      end_date: "",
      position: "",
      supervisor: "",
      schedule: "",
      reason_for_leaving: "",
      verified: false,
    })

  useEffect(() => {
    if (open && editingRecord) {
      setData({
        company_name: editingRecord.company_name || "",
        address: editingRecord.address || "",
        city: editingRecord.city || "",
        state: editingRecord.state || "",
        zip: editingRecord.zip || "",
        phone: editingRecord.phone || "",
        start_date: editingRecord.start_date || "",
        end_date: editingRecord.end_date || "",
        position: editingRecord.position || "",
        supervisor: editingRecord.supervisor || "",
        schedule: editingRecord.schedule || "",
        reason_for_leaving: editingRecord.reason_for_leaving || "",
        verified: editingRecord.verified ?? false,
      })
    } else if (open && !editingRecord) {
      reset()
    }
  }, [open, editingRecord?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  transform((data) => ({ subject_employment_record: data }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const basePath = `/cases/${caseId}/subjects/${subjectId}/employment_records`
    const options = {
      onSuccess: () => {
        reset()
        onClose()
      },
    }

    if (isEditing) {
      patch(`${basePath}/${editingRecord.id}`, options)
    } else {
      post(basePath, options)
    }
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Edit" : "Add"} Employment Record
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <FormRow>
            <Label>Company Name</Label>
            <Input
              value={data.company_name}
              onChange={(e) => setData("company_name", e.target.value)}
            />
            <FieldError error={errors.company_name} />
          </FormRow>
          <FormRow>
            <Label>Address</Label>
            <Input
              value={data.address}
              onChange={(e) => setData("address", e.target.value)}
            />
          </FormRow>
          <FormGrid>
            <FormRow>
              <Label>City</Label>
              <Input
                value={data.city}
                onChange={(e) => setData("city", e.target.value)}
              />
            </FormRow>
            <FormGrid>
              <FormRow>
                <Label>State</Label>
                <Input
                  value={data.state}
                  onChange={(e) => setData("state", e.target.value)}
                  maxLength={2}
                />
              </FormRow>
              <FormRow>
                <Label>Zip</Label>
                <Input
                  value={data.zip}
                  onChange={(e) => setData("zip", e.target.value)}
                />
              </FormRow>
            </FormGrid>
          </FormGrid>
          <FormRow>
            <Label>Phone</Label>
            <Input
              value={data.phone}
              onChange={(e) => setData("phone", e.target.value)}
            />
          </FormRow>
          <FormGrid>
            <FormRow>
              <Label>Start Date</Label>
              <Input
                type="date"
                value={data.start_date}
                onChange={(e) => setData("start_date", e.target.value)}
              />
            </FormRow>
            <FormRow>
              <Label>End Date</Label>
              <Input
                type="date"
                value={data.end_date}
                onChange={(e) => setData("end_date", e.target.value)}
              />
            </FormRow>
          </FormGrid>
          <FormRow>
            <Label>Position</Label>
            <Input
              value={data.position}
              onChange={(e) => setData("position", e.target.value)}
            />
          </FormRow>
          <FormRow>
            <Label>Supervisor</Label>
            <Input
              value={data.supervisor}
              onChange={(e) => setData("supervisor", e.target.value)}
            />
          </FormRow>
          <FormRow>
            <Label>Schedule</Label>
            <Input
              value={data.schedule}
              onChange={(e) => setData("schedule", e.target.value)}
            />
          </FormRow>
          <FormRow>
            <Label>Reason for Leaving</Label>
            <Input
              value={data.reason_for_leaving}
              onChange={(e) => setData("reason_for_leaving", e.target.value)}
            />
          </FormRow>
          <div className="flex items-center gap-2">
            <Checkbox
              id="emp-verified"
              checked={data.verified}
              onCheckedChange={(v) => setData("verified", v === true)}
            />
            <Label htmlFor="emp-verified">Verified</Label>
          </div>
          <DialogFooter>
            <Button variant="secondary" type="button" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={processing}
              className="bg-hpi-active-blue hover:bg-hpi-dark-blue text-white"
            >
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/* ── Criminal Record Modal ──────────────────────────────── */

function AddCriminalModal({
  open,
  onClose,
  caseId,
  subjectId,
  editingRecord,
}: ModalProps) {
  const isEditing = !!editingRecord
  const { data, setData, post, patch, processing, errors, reset, transform } =
    useForm({
      offense_date: "",
      county: "",
      state: "",
      conviction: "",
      disposition: "",
      notes: "",
      verified: false,
    })

  useEffect(() => {
    if (open && editingRecord) {
      setData({
        offense_date: editingRecord.offense_date || "",
        county: editingRecord.county || "",
        state: editingRecord.state || "",
        conviction: editingRecord.conviction || "",
        disposition: editingRecord.disposition || "",
        notes: editingRecord.notes || "",
        verified: editingRecord.verified ?? false,
      })
    } else if (open && !editingRecord) {
      reset()
    }
  }, [open, editingRecord?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  transform((data) => ({ subject_criminal_record: data }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const basePath = `/cases/${caseId}/subjects/${subjectId}/criminal_records`
    const options = {
      onSuccess: () => {
        reset()
        onClose()
      },
    }

    if (isEditing) {
      patch(`${basePath}/${editingRecord.id}`, options)
    } else {
      post(basePath, options)
    }
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Edit" : "Add"} Criminal Record
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <FormRow>
            <Label>Offense Date</Label>
            <Input
              type="date"
              value={data.offense_date}
              onChange={(e) => setData("offense_date", e.target.value)}
            />
            <FieldError error={errors.offense_date} />
          </FormRow>
          <FormGrid>
            <FormRow>
              <Label>County</Label>
              <Input
                value={data.county}
                onChange={(e) => setData("county", e.target.value)}
              />
            </FormRow>
            <FormRow>
              <Label>State</Label>
              <Input
                value={data.state}
                onChange={(e) => setData("state", e.target.value)}
                maxLength={2}
              />
            </FormRow>
          </FormGrid>
          <FormRow>
            <Label>Conviction</Label>
            <Input
              value={data.conviction}
              onChange={(e) => setData("conviction", e.target.value)}
            />
            <FieldError error={errors.conviction} />
          </FormRow>
          <FormRow>
            <Label>Disposition</Label>
            <Input
              value={data.disposition}
              onChange={(e) => setData("disposition", e.target.value)}
            />
          </FormRow>
          <FormRow>
            <Label>Notes</Label>
            <Textarea
              value={data.notes}
              onChange={(e) => setData("notes", e.target.value)}
              rows={2}
            />
          </FormRow>
          <div className="flex items-center gap-2">
            <Checkbox
              id="crim-verified"
              checked={data.verified}
              onCheckedChange={(v) => setData("verified", v === true)}
            />
            <Label htmlFor="crim-verified">Verified</Label>
          </div>
          <DialogFooter>
            <Button variant="secondary" type="button" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={processing}
              className="bg-hpi-active-blue hover:bg-hpi-dark-blue text-white"
            >
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/* ── Education Modal ────────────────────────────────────── */

function AddEducationModal({
  open,
  onClose,
  caseId,
  subjectId,
  editingRecord,
}: ModalProps) {
  const isEditing = !!editingRecord
  const { data, setData, post, patch, processing, errors, reset, transform } =
    useForm({
      institution: "",
      city: "",
      state: "",
      start_date: "",
      end_date: "",
      degree: "",
      major: "",
      verified: false,
    })

  useEffect(() => {
    if (open && editingRecord) {
      setData({
        institution: editingRecord.institution || "",
        city: editingRecord.city || "",
        state: editingRecord.state || "",
        start_date: editingRecord.start_date || "",
        end_date: editingRecord.end_date || "",
        degree: editingRecord.degree || "",
        major: editingRecord.major || "",
        verified: editingRecord.verified ?? false,
      })
    } else if (open && !editingRecord) {
      reset()
    }
  }, [open, editingRecord?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  transform((data) => ({ subject_education_record: data }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const basePath = `/cases/${caseId}/subjects/${subjectId}/education_records`
    const options = {
      onSuccess: () => {
        reset()
        onClose()
      },
    }

    if (isEditing) {
      patch(`${basePath}/${editingRecord.id}`, options)
    } else {
      post(basePath, options)
    }
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Edit" : "Add"} Education Record
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <FormRow>
            <Label>Institution</Label>
            <Input
              value={data.institution}
              onChange={(e) => setData("institution", e.target.value)}
            />
            <FieldError error={errors.institution} />
          </FormRow>
          <FormGrid>
            <FormRow>
              <Label>City</Label>
              <Input
                value={data.city}
                onChange={(e) => setData("city", e.target.value)}
              />
            </FormRow>
            <FormRow>
              <Label>State</Label>
              <Input
                value={data.state}
                onChange={(e) => setData("state", e.target.value)}
                maxLength={2}
              />
            </FormRow>
          </FormGrid>
          <FormGrid>
            <FormRow>
              <Label>Start Date</Label>
              <Input
                type="date"
                value={data.start_date}
                onChange={(e) => setData("start_date", e.target.value)}
              />
            </FormRow>
            <FormRow>
              <Label>End Date</Label>
              <Input
                type="date"
                value={data.end_date}
                onChange={(e) => setData("end_date", e.target.value)}
              />
            </FormRow>
          </FormGrid>
          <FormRow>
            <Label>Degree</Label>
            <Input
              value={data.degree}
              onChange={(e) => setData("degree", e.target.value)}
            />
          </FormRow>
          <FormRow>
            <Label>Major</Label>
            <Input
              value={data.major}
              onChange={(e) => setData("major", e.target.value)}
            />
          </FormRow>
          <div className="flex items-center gap-2">
            <Checkbox
              id="edu-verified"
              checked={data.verified}
              onCheckedChange={(v) => setData("verified", v === true)}
            />
            <Label htmlFor="edu-verified">Verified</Label>
          </div>
          <DialogFooter>
            <Button variant="secondary" type="button" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={processing}
              className="bg-hpi-active-blue hover:bg-hpi-dark-blue text-white"
            >
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/* ── Orchestrator Component ─────────────────────────────── */

export type SubjectModalType =
  | "address"
  | "alias"
  | "contact_email"
  | "contact_phone"
  | "contact_social"
  | "vehicle"
  | "employment"
  | "criminal"
  | "education"
  | null

interface SubjectAddModalsProps {
  activeModal: SubjectModalType
  onClose: () => void
  caseId: number
  subjectId: number
  contactTypes: ContactTypes
  editingRecord?: EditingRecord
}

export function SubjectAddModals({
  activeModal,
  onClose,
  caseId,
  subjectId,
  contactTypes,
  editingRecord,
}: SubjectAddModalsProps) {
  const baseProps = { caseId, subjectId, onClose }

  return (
    <>
      <AddAddressModal
        open={activeModal === "address"}
        editingRecord={editingRecord}
        {...baseProps}
      />
      <AddAliasModal
        open={activeModal === "alias"}
        editingRecord={editingRecord}
        {...baseProps}
      />
      <AddContactModal
        open={activeModal === "contact_email"}
        contactTypes={contactTypes.email}
        category="email"
        editingRecord={editingRecord}
        {...baseProps}
      />
      <AddContactModal
        open={activeModal === "contact_phone"}
        contactTypes={contactTypes.phone}
        category="phone"
        editingRecord={editingRecord}
        {...baseProps}
      />
      <AddContactModal
        open={activeModal === "contact_social"}
        contactTypes={contactTypes.social}
        category="social"
        editingRecord={editingRecord}
        {...baseProps}
      />
      <AddVehicleModal
        open={activeModal === "vehicle"}
        editingRecord={editingRecord}
        {...baseProps}
      />
      <AddEmploymentModal
        open={activeModal === "employment"}
        editingRecord={editingRecord}
        {...baseProps}
      />
      <AddCriminalModal
        open={activeModal === "criminal"}
        editingRecord={editingRecord}
        {...baseProps}
      />
      <AddEducationModal
        open={activeModal === "education"}
        editingRecord={editingRecord}
        {...baseProps}
      />
    </>
  )
}
